import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus, FileText, Upload, CheckCircle, Clock, XCircle, ChevronRight,
  ArrowLeft, Paperclip, Download, Search, User, Shield, CreditCard,
  Trash2, Mail, Send, Hash, Link2, AlertTriangle, Hourglass, ExternalLink, MessageCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import RicercaVeicoli from './RicercaVeicoli';

const STATI = {
  in_attesa: { label: 'In Attesa', color: '#F59E0B', bg: '#FEF3C7', icon: Clock },
  presa_in_carico: { label: 'Presa in Carico', color: '#3B82F6', bg: '#DBEAFE', icon: Shield },
  richiesta_pagamento: { label: 'Richiesta Pagamento', color: '#A855F7', bg: '#F3E8FF', icon: CreditCard },
  pagamento_posticipato: { label: 'Pagamento Posticipato', color: '#D97706', bg: '#FEF3C7', icon: Clock },
  pagamento_allegato: { label: 'Pagamento Allegato', color: '#8B5CF6', bg: '#EDE9FE', icon: Upload },
  pagamento_accettato: { label: 'Pagamento Accettato', color: '#7C3AED', bg: '#EDE9FE', icon: CheckCircle },
  incasso_inviato: { label: 'Incasso Inviato', color: '#F97316', bg: '#FFF7ED', icon: Mail },
  emessa_da_pagare: { label: 'Emessa - Da Pagare', color: '#E11D48', bg: '#FFE4E6', icon: AlertTriangle },
  emessa: { label: 'Emessa', color: '#10B981', bg: '#D1FAE5', icon: CheckCircle },
  rifiutata: { label: 'Rifiutata', color: '#EF4444', bg: '#FEE2E2', icon: XCircle },
};

const TIPI_POLIZZA = ['Auto', 'Moto', 'Casa', 'Vita', 'Infortuni', 'RC Professionale', 'Polizza Azienda', 'Salute', 'Viaggio', 'Altro'];

const StatoBadge = ({ stato }) => {
  const s = STATI[stato] || STATI.in_attesa;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: s.bg, color: s.color }} data-testid={`stato-badge-${stato}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
};

export const RichiesteEmissione = () => {
  const { user, isAdmin } = useAuth();
  const isCollaboratore = user?.ruolo === 'collaboratore';
  const isStaff = user?.ruolo === 'super_admin' || user?.ruolo === 'dipendente';
  const [richieste, setRichieste] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [propostaOpen, setPropostaOpen] = useState(false);
  const [filterStato, setFilterStato] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('richieste');
  const fileRef = useRef(null);

  const [form, setForm] = useState({ cliente_nome: '', cliente_cognome: '', compagnia: '', tipo_polizza: 'Auto', premio: '', premio_riservato: '', tipo_premio: 'annuale', targa: '', telefono_cliente: '', note: '', bersani: 'no', numero_preventivo: '', assegna_a: '' });
  const [formFile, setFormFile] = useState(null);
  const [docFiles, setDocFiles] = useState({ libretto: null, documento_identita: null, tessera_sanitaria: null, patente: null, stato_famiglia: null, libretto_bersani: null });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [collaboratori, setCollaboratori] = useState([]);

  // Proposta
  const [numeroProposta, setNumeroProposta] = useState('');

  // Email incasso
  const [emailForm, setEmailForm] = useState({ email_to: '', oggetto: '', corpo: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Notifica collaboratore / Comunicazioni
  const [notificaOpen, setNotificaOpen] = useState(false);
  const [notificaMsg, setNotificaMsg] = useState('');
  const [sendingNotifica, setSendingNotifica] = useState(false);
  const [comMsg, setComMsg] = useState('');
  const [sendingCom, setSendingCom] = useState(false);

  // Fabrick Payment Link
  const [fabrickOpen, setFabrickOpen] = useState(false);
  const [fabrickForm, setFabrickForm] = useState({ importo: '', email_cliente: '' });
  const [generatingLink, setGeneratingLink] = useState(false);

  // Date filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { fetchAll(); if (isStaff) fetchCollaboratori(); }, []);

  const fetchAll = async () => {
    try {
      const [r, s] = await Promise.all([api.get('/richieste-emissione'), api.get('/richieste-emissione/stats')]);
      setRichieste(r.data); setStats(s.data.stats || {});
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };

  const fetchCollaboratori = async () => {
    try { const r = await api.get('/users'); setCollaboratori((r.data || []).filter(u => u.ruolo === 'collaboratore')); } catch (e) { console.error('Errore caricamento collaboratori:', e); }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('cliente_nome', form.cliente_nome);
      fd.append('cliente_cognome', form.cliente_cognome);
      fd.append('compagnia', form.compagnia);
      fd.append('tipo_polizza', form.tipo_polizza);
      if (form.premio) fd.append('premio', form.premio);
      if (form.premio_riservato) fd.append('premio_riservato', form.premio_riservato);
      if (form.tipo_premio) fd.append('tipo_premio', form.tipo_premio);
      if (form.targa) fd.append('targa', form.targa);
      if (form.telefono_cliente) fd.append('telefono_cliente', form.telefono_cliente);
      let noteText = form.note || '';
      if (form.bersani !== 'no') noteText = `[Bersani: ${form.bersani === 'stesso' ? 'Stesso proprietario' : 'Diverso proprietario'}]\n${noteText}`;
      if (noteText) fd.append('note', noteText);
      if (form.numero_preventivo) fd.append('numero_preventivo', form.numero_preventivo);
      if (form.assegna_a) fd.append('assegna_a', form.assegna_a);
      if (formFile) fd.append('file', formFile);
      await api.post('/richieste-emissione', fd, { headers: { 'Content-Type': 'multipart/form-data' } });

      const lastRes = await api.get('/richieste-emissione');
      const newRichiesta = lastRes.data[0];
      if (newRichiesta) {
        const docUploads = [
          { key: 'libretto', tipo: 'libretto_assicurare' },
          { key: 'documento_identita', tipo: 'documento_identita' },
          { key: 'tessera_sanitaria', tipo: 'tessera_sanitaria' },
          { key: 'patente', tipo: 'patente' },
          { key: 'stato_famiglia', tipo: 'stato_famiglia' },
          { key: 'libretto_bersani', tipo: 'libretto_bersani' },
        ];
        for (const { key, tipo } of docUploads) {
          if (docFiles[key]) {
            const dfd = new FormData();
            dfd.append('tipo', tipo);
            dfd.append('file', docFiles[key]);
            await api.post(`/richieste-emissione/${newRichiesta.id}/upload`, dfd, { headers: { 'Content-Type': 'multipart/form-data' } });
          }
        }
      }

      toast.success('Richiesta creata!'); setCreateOpen(false);
      setForm({ cliente_nome: '', cliente_cognome: '', compagnia: '', tipo_polizza: 'Auto', premio: '', premio_riservato: '', tipo_premio: 'annuale', targa: '', telefono_cliente: '', note: '', bersani: 'no', numero_preventivo: '', assegna_a: '' });
      setFormFile(null); setDocFiles({ libretto: null, documento_identita: null, tessera_sanitaria: null, patente: null, stato_famiglia: null, libretto_bersani: null });
      fetchAll();
    } catch { toast.error('Errore creazione'); } finally { setSubmitting(false); }
  };

  const handleUpload = async (richiestaId, tipo) => {
    const input = document.createElement('input'); input.type = 'file'; input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
    input.onchange = async (e) => {
      const file = e.target.files[0]; if (!file) return; setUploadingType(tipo);
      try {
        const fd = new FormData(); fd.append('tipo', tipo); fd.append('file', file);
        const res = await api.post(`/richieste-emissione/${richiestaId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setSelected(res.data); toast.success('Documento caricato!'); fetchAll();
      } catch { toast.error('Errore upload'); } finally { setUploadingType(null); }
    }; input.click();
  };

  const handleStatoChange = async (richiestaId, nuovoStato, extraData = {}) => {
    try {
      const res = await api.patch(`/richieste-emissione/${richiestaId}/stato`, { stato: nuovoStato, ...extraData });
      setSelected(res.data); toast.success(`Stato: ${STATI[nuovoStato]?.label}`); fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handlePrendiInCarico = () => {
    if (!selected) return;
    setNumeroProposta(''); setPropostaOpen(true);
  };

  const confirmPrendiInCarico = () => {
    handleStatoChange(selected.id, 'presa_in_carico', { numero_proposta: numeroProposta || null });
    setPropostaOpen(false);
  };

  const handleOpenEmail = () => {
    if (!selected) return;
    const np = selected.numero_proposta || 'N/D';
    const cliente = `${selected.cliente_nome} ${selected.cliente_cognome}`;
    const premioIncasso = selected.premio_riservato || selected.premio || '';
    setEmailForm({
      email_to: '',
      oggetto: `Richiesta Incasso - Proposta N. ${np} - ${cliente}`,
      corpo: `Con la presente si richiede l'incasso per la proposta N. ${np} intestata a ${cliente}.\n\nCompagnia: ${selected.compagnia}\n${premioIncasso ? `Premio: EUR ${premioIncasso}\n` : ''}${selected.targa ? `Targa: ${selected.targa}\n` : ''}\nIn allegato la documentazione.\n\nCordiali saluti`,
    });
    setEmailOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.email_to.trim()) { toast.error('Inserisci email destinatario'); return; }
    setSendingEmail(true);
    try {
      const res = await api.post(`/richieste-emissione/${selected.id}/invia-email-incasso`, emailForm);
      if (res.data.success) {
        toast.success('Email incasso inviata!'); setEmailOpen(false);
        const updated = await api.get('/richieste-emissione');
        const fresh = updated.data.find(r => r.id === selected.id);
        if (fresh) setSelected(fresh);
        fetchAll();
      } else { toast.error(res.data.error || 'Errore invio'); }
    } catch { toast.error('Errore invio email'); } finally { setSendingEmail(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    try { await api.delete(`/richieste-emissione/${id}`); setSelected(null); toast.success('Eliminata'); fetchAll(); } catch { toast.error('Errore'); }
  };

  const handleOpenNotifica = () => {
    if (!selected) return;
    const LABELS = { in_attesa: 'In Attesa', presa_in_carico: 'Presa in Carico', pagamento_allegato: 'Pagamento Allegato', incasso_inviato: 'Incasso Inviato', emessa: 'Emessa', rifiutata: 'Rifiutata' };
    const stato = LABELS[selected.stato] || selected.stato;
    setNotificaMsg(`La tua richiesta per ${selected.cliente_nome} ${selected.cliente_cognome} - ${selected.compagnia} è stata aggiornata.\n\nStato attuale: ${stato}${selected.numero_proposta ? `\nN. Proposta: ${selected.numero_proposta}` : ''}`);
    setNotificaOpen(true);
  };

  const handleSendNotifica = async () => {
    setSendingNotifica(true);
    try {
      const res = await api.post(`/richieste-emissione/${selected.id}/notifica-collaboratore`, { messaggio: notificaMsg });
      if (res.data.success) { toast.success('Notifica inviata al collaboratore!'); setNotificaOpen(false); refreshSelected(); }
      else toast.error(res.data.error || 'Errore');
    } catch { toast.error('Errore invio'); } finally { setSendingNotifica(false); }
  };

  const handleSendComunicazione = async () => {
    if (!comMsg.trim()) return;
    setSendingCom(true);
    try {
      const res = await api.post(`/richieste-emissione/${selected.id}/comunicazione`, { messaggio: comMsg });
      if (res.data.success) { toast.success('Comunicazione inviata!'); setComMsg(''); refreshSelected(); }
      else toast.error(res.data.error || 'Errore');
    } catch { toast.error('Errore invio'); } finally { setSendingCom(false); }
  };

  const handlePosticipaPagamento = () => {
    if (!selected) return;
    handleStatoChange(selected.id, 'pagamento_posticipato');
  };

  const handleOpenFabrick = () => {
    if (!selected) return;
    setFabrickForm({ importo: selected.premio || '', email_cliente: '' });
    setFabrickOpen(true);
  };

  const handleGeneraLinkPagamento = async () => {
    if (!fabrickForm.importo) { toast.error('Inserisci importo'); return; }
    setGeneratingLink(true);
    try {
      const res = await api.post(`/richieste-emissione/${selected.id}/genera-link-pagamento`, fabrickForm);
      if (res.data.success) {
        toast.success('Link di pagamento generato!');
        setFabrickOpen(false);
        refreshSelected();
      } else { toast.error(res.data.error || 'Errore generazione link'); }
    } catch { toast.error('Errore generazione link'); } finally { setGeneratingLink(false); }
  };

  const handleSendLinkWA = async () => {
    if (!selected) return;
    const tel = selected.telefono_cliente;
    if (!tel) { toast.error('Nessun telefono cliente. Aggiungilo dalla pratica.'); return; }
    if (!selected.payment_link) { toast.error('Genera prima il link di pagamento Fabrick.'); return; }
    try {
      const res = await api.post(`/richieste-emissione/${selected.id}/invia-link-wa`, { telefono: tel });
      if (res.data.success) { toast.success('Link inviato via WhatsApp!'); refreshSelected(); }
      else toast.error(res.data.error || 'Errore invio');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio WhatsApp'); }
  };

  const handleUpdateField = async (field, value) => {
    if (!selected) return;
    try {
      const res = await api.patch(`/richieste-emissione/${selected.id}/aggiorna`, { [field]: value });
      setSelected(res.data);
      fetchAll();
      toast.success('Aggiornato!');
    } catch { toast.error('Errore aggiornamento'); }
  };

  const refreshSelected = async () => {
    const updated = await api.get('/richieste-emissione');
    const fresh = updated.data.find(r => r.id === selected.id);
    if (fresh) setSelected(fresh);
    fetchAll();
  };

  const filtered = richieste.filter(r => {
    if (filterStato && r.stato !== filterStato) return false;
    if (dateFrom) {
      const rDate = (r.created_at || '').slice(0, 10);
      if (rDate < dateFrom) return false;
    }
    if (dateTo) {
      const rDate = (r.created_at || '').slice(0, 10);
      if (rDate > dateTo) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return `${r.cliente_nome} ${r.cliente_cognome} ${r.compagnia} ${r.tipo_polizza} ${r.targa || ''} ${r.numero_proposta || ''} ${r.collaboratore_nome || ''}`.toLowerCase().includes(q);
    }
    return true;
  });

  const fmtDate = (d) => d ? format(new Date(d), 'dd/MM/yyyy HH:mm', { locale: it }) : '';

  const DocUploadField = ({ label, fileKey, required }) => (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-xs text-slate-600 flex-1">{label}{required ? ' *' : ''}</Label>
      <div className="flex items-center gap-2">
        {docFiles[fileKey] ? (
          <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
            <CheckCircle size={12} /> <span className="truncate max-w-[120px]">{docFiles[fileKey].name}</span>
            <button onClick={() => setDocFiles(p => ({ ...p, [fileKey]: null }))} className="text-red-400 hover:text-red-600"><XCircle size={12} /></button>
          </div>
        ) : (
          <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => {
            const input = document.createElement('input'); input.type = 'file'; input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
            input.onchange = (e) => { if (e.target.files[0]) setDocFiles(p => ({ ...p, [fileKey]: e.target.files[0] })); };
            input.click();
          }} data-testid={`doc-upload-${fileKey}`}>
            <Upload size={12} className="mr-1" /> Carica
          </Button>
        )}
      </div>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-10 w-10 border-b-2 border-slate-600 rounded-full" /></div>;

  // ═══ DETAIL VIEW ═══
  if (selected) {
    const r = selected;
    return (
      <div className="p-3 sm:p-6 md:p-8 max-w-4xl mx-auto" data-testid="richiesta-detail">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-6" data-testid="back-btn">
          <ArrowLeft size={16} /> Torna alla lista
        </button>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{r.cliente_nome} {r.cliente_cognome}</h2>
                <p className="text-sm text-slate-500 mt-0.5">{r.compagnia} · {r.tipo_polizza}{r.targa ? ` · ${r.targa}` : ''}</p>
              </div>
              <StatoBadge stato={r.stato} />
            </div>
            <div className="flex items-center gap-4 flex-wrap mt-3">
              {r.premio && <p className="text-2xl font-bold text-slate-900">€ {r.premio} <span className="text-sm font-normal text-slate-400">/{r.tipo_premio || 'annuale'}</span></p>}
              {isStaff && r.premio_riservato && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200" data-testid="premio-riservato-badge">
                  <CreditCard size={14} className="text-red-600" />
                  <span className="text-sm font-semibold text-red-700">Riservato: € {r.premio_riservato}</span>
                </div>
              )}
              {r.numero_proposta && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                  <Hash size={14} className="text-blue-600" />
                  <span className="text-sm font-semibold text-blue-700" data-testid="numero-proposta">{r.numero_proposta}</span>
                </div>
              )}
              {r.numero_preventivo && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
                  <FileText size={14} className="text-amber-600" />
                  <span className="text-sm font-semibold text-amber-700">Prev. {r.numero_preventivo}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400 flex-wrap">
              <span>Creata: {fmtDate(r.created_at)}</span>
              <span>Da: {r.collaboratore_nome}</span>
              {r.admin_nome && <span>Gestita da: {r.admin_nome}</span>}
              {r.email_incasso && <span>Email incasso: {r.email_incasso}</span>}
              {r.telefono_cliente && <span>Tel. cliente: {r.telefono_cliente}</span>}
            </div>
            {/* Editable fields for staff */}
            {isStaff && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <div className="flex items-center gap-1.5 bg-white rounded-lg px-3 py-1.5 border border-slate-300 shadow-sm">
                  <span className="text-[11px] text-slate-500 whitespace-nowrap">Premio Cliente €</span>
                  <input
                    type="text" key={`premio-${r.id}-${r.premio}`} defaultValue={r.premio || ''}
                    onBlur={e => { const v = e.target.value.trim(); if (v !== (r.premio || '')) handleUpdateField('premio', v); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="—"
                    className="w-24 text-sm font-bold text-slate-900 bg-transparent border-none outline-none"
                    data-testid="edit-premio-cliente"
                  />
                </div>
                <div className="flex items-center gap-1.5 bg-red-50 rounded-lg px-3 py-1.5 border border-red-300 shadow-sm">
                  <span className="text-[11px] text-red-600 whitespace-nowrap font-medium">Riservato €</span>
                  <input
                    type="text" key={`ris-${r.id}-${r.premio_riservato}`} defaultValue={r.premio_riservato || ''}
                    onBlur={e => { const v = e.target.value.trim(); if (v !== (r.premio_riservato || '')) handleUpdateField('premio_riservato', v); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="Inserisci..."
                    className="w-24 text-sm font-bold text-red-700 bg-transparent border-none outline-none placeholder:text-red-300 placeholder:font-normal"
                    data-testid="edit-premio-riservato"
                  />
                </div>
                <div className="flex items-center gap-1.5 bg-white rounded-lg px-3 py-1.5 border border-slate-300 shadow-sm">
                  <span className="text-[11px] text-slate-500 whitespace-nowrap">Tel. Cliente</span>
                  <input
                    type="text" key={`tel-${r.id}-${r.telefono_cliente}`} defaultValue={r.telefono_cliente || ''}
                    onBlur={e => { const v = e.target.value.trim(); if (v !== (r.telefono_cliente || '')) handleUpdateField('telefono_cliente', v); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="—"
                    className="w-32 text-sm font-semibold text-slate-700 bg-transparent border-none outline-none"
                    data-testid="edit-telefono-cliente"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-3 sm:p-6 border-b border-slate-100 flex flex-wrap gap-1.5 sm:gap-2">
            {/* Step 1: Prendi in carico */}
            {isStaff && r.stato === 'in_attesa' && (
              <Button size="sm" onClick={handlePrendiInCarico} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-prendi-carico">
                <Shield size={14} className="mr-1.5" /> Prendi in Carico
              </Button>
            )}
            {/* Step 2: Richiedi pagamento al collaboratore */}
            {isStaff && r.stato === 'presa_in_carico' && (
              <Button size="sm" onClick={() => handleStatoChange(r.id, 'richiesta_pagamento')} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-richiedi-pagamento">
                <CreditCard size={14} className="mr-1.5" /> Richiedi Pagamento
              </Button>
            )}
            {/* Step 2b: Posticipa pagamento */}
            {isStaff && r.stato === 'presa_in_carico' && (
              <Button size="sm" variant="outline" onClick={handlePosticipaPagamento} className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="btn-posticipa-pagamento">
                <Hourglass size={14} className="mr-1.5" /> Posticipa Pagamento
              </Button>
            )}
            {/* Step 2d: Admin prosegue senza pagamento */}
            {isStaff && r.stato === 'presa_in_carico' && (
              <Button size="sm" variant="outline" onClick={() => handleStatoChange(r.id, 'pagamento_accettato')} className="border-green-400 text-green-700 hover:bg-green-50" data-testid="btn-prosegui-senza-pagamento">
                <CheckCircle size={14} className="mr-1.5" /> Prosegui senza Pagamento
              </Button>
            )}
            {/* Collaborator: reminder banner for posticipato */}
            {r.stato === 'pagamento_posticipato' && isCollaboratore && (
              <div className="w-full p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3 mb-1">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800">Manca il pagamento</p>
                  <p className="text-xs text-amber-600">Allega il pagamento per completare la pratica</p>
                </div>
                <Button size="sm" onClick={() => handleUpload(r.id, 'pagamento_collaboratore')} disabled={!!uploadingType} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-allega-pagamento-posticipato">
                  <Upload size={14} className="mr-1.5" /> {uploadingType === 'pagamento_collaboratore' ? 'Caricamento...' : 'Allega Pagamento'}
                </Button>
              </div>
            )}
            {/* Collaboratore allega pagamento (richiesta_pagamento / posticipato) */}
            {(r.stato === 'richiesta_pagamento' || r.stato === 'pagamento_posticipato') && isCollaboratore && (
              <Button size="sm" variant="outline" onClick={() => handleUpload(r.id, 'pagamento_collaboratore')} disabled={!!uploadingType} data-testid="btn-allega-pagamento-collab">
                <Upload size={14} className="mr-1.5" /> {uploadingType === 'pagamento_collaboratore' ? 'Caricamento...' : 'Allega Pagamento'}
              </Button>
            )}

            {/* === STAFF: Pagamenti separati === */}
            {isStaff && r.stato !== 'in_attesa' && r.stato !== 'emessa' && r.stato !== 'rifiutata' && (
              <div className="w-full flex flex-wrap gap-1.5 sm:gap-2 pt-1 border-t border-slate-100 mt-1">
                {/* Pagamento Collaboratore → quello che il collab paga a te */}
                <Button size="sm" variant="outline" onClick={() => handleUpload(r.id, 'pagamento_collaboratore')} disabled={!!uploadingType} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="btn-allega-pag-collab">
                  <Upload size={14} className="mr-1.5" /> {uploadingType === 'pagamento_collaboratore' ? 'Caricamento...' : 'Pag. Collaboratore'}
                </Button>
                {/* Pagamento Direzionale → quello che tu mandi alla compagnia */}
                <Button size="sm" variant="outline" onClick={() => handleUpload(r.id, 'pagamento_direzionale')} disabled={!!uploadingType} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="btn-allega-pag-direz">
                  <Upload size={14} className="mr-1.5" /> {uploadingType === 'pagamento_direzionale' ? 'Caricamento...' : 'Pag. Direzionale'}
                </Button>
              </div>
            )}

            {/* Admin conferma pagamento accettato */}
            {isStaff && (r.stato === 'richiesta_pagamento' || r.stato === 'pagamento_posticipato' || r.stato === 'pagamento_allegato') && (
              <Button size="sm" onClick={() => handleStatoChange(r.id, 'pagamento_accettato')} className="bg-violet-600 hover:bg-violet-700" data-testid="btn-pagamento-accettato">
                <CheckCircle size={14} className="mr-1.5" /> Pagamento Accettato
              </Button>
            )}
            {/* Step 4: Invia email incasso alla compagnia */}
            {isStaff && (r.stato === 'pagamento_accettato' || r.stato === 'pagamento_posticipato' || r.stato === 'pagamento_allegato' || r.stato === 'presa_in_carico') && (
              <Button size="sm" onClick={handleOpenEmail} className="bg-orange-500 hover:bg-orange-600" data-testid="btn-invia-incasso">
                <Mail size={14} className="mr-1.5" /> Richiesta Incasso
              </Button>
            )}
            {/* Step 5: Emetti polizza */}
            {isStaff && (r.stato === 'incasso_inviato' || r.stato === 'pagamento_accettato') && (
              <>
                <Button size="sm" variant="outline" onClick={() => handleUpload(r.id, 'polizza')} disabled={!!uploadingType} data-testid="btn-allega-polizza">
                  <FileText size={14} className="mr-1.5" /> {uploadingType === 'polizza' ? 'Caricamento...' : 'Allega Polizza'}
                </Button>
                {(r.documenti || []).some(d => d.tipo === 'pagamento_collaboratore') ? (
                  <Button size="sm" onClick={() => handleStatoChange(r.id, 'emessa')} className="bg-green-600 hover:bg-green-700" data-testid="btn-emetti">
                    <CheckCircle size={14} className="mr-1.5" /> Emetti Polizza
                  </Button>
                ) : (
                  <>
                    <Button size="sm" onClick={() => handleStatoChange(r.id, 'emessa_da_pagare')} className="bg-rose-600 hover:bg-rose-700" data-testid="btn-emetti-da-pagare">
                      <AlertTriangle size={14} className="mr-1.5" /> Emetti - Da Pagare
                    </Button>
                    <Button size="sm" onClick={() => handleStatoChange(r.id, 'emessa')} variant="outline" className="border-green-400 text-green-700 hover:bg-green-50" data-testid="btn-emetti">
                      <CheckCircle size={14} className="mr-1.5" /> Emetti (Pagata)
                    </Button>
                  </>
                )}
              </>
            )}
            {/* Post-emissione: emessa_da_pagare */}
            {isStaff && r.stato === 'emessa_da_pagare' && (
              <div className="w-full p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-3 mb-1">
                <AlertTriangle size={18} className="text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">Polizza emessa - Pagamento collaboratore non pervenuto</p>
                  <p className="text-xs text-red-600">Richiedi il pagamento al collaboratore</p>
                </div>
              </div>
            )}
            {isStaff && r.stato === 'emessa_da_pagare' && (
              <>
                <Button size="sm" onClick={() => handleStatoChange(r.id, 'richiesta_pagamento')} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-richiedi-pagamento-post">
                  <CreditCard size={14} className="mr-1.5" /> Richiedi Pagamento
                </Button>
                <Button size="sm" onClick={handleOpenEmail} className="bg-orange-500 hover:bg-orange-600" data-testid="btn-incasso-post">
                  <Mail size={14} className="mr-1.5" /> Richiesta Incasso
                </Button>
                {(r.documenti || []).some(d => d.tipo === 'pagamento_collaboratore') && (
                  <Button size="sm" onClick={() => handleStatoChange(r.id, 'emessa')} className="bg-green-600 hover:bg-green-700" data-testid="btn-conferma-pagata">
                    <CheckCircle size={14} className="mr-1.5" /> Conferma Pagata
                  </Button>
                )}
              </>
            )}
            {isStaff && r.stato === 'emessa' && (
              <Button size="sm" variant="outline" onClick={() => handleUpload(r.id, 'polizza')} disabled={!!uploadingType} data-testid="btn-allega-polizza-emessa">
                <FileText size={14} className="mr-1.5" /> {uploadingType === 'polizza' ? 'Caricamento...' : 'Allega Polizza'}
              </Button>
            )}
            {isStaff && r.stato !== 'emessa' && r.stato !== 'emessa_da_pagare' && r.stato !== 'rifiutata' && (
              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleStatoChange(r.id, 'rifiutata')} data-testid="btn-rifiuta">
                <XCircle size={14} className="mr-1.5" /> Rifiuta
              </Button>
            )}
            {/* Retrocedi stato */}
            {isStaff && r.stato !== 'in_attesa' && r.stato !== 'rifiutata' && (() => {
              const PREV = { presa_in_carico: 'in_attesa', richiesta_pagamento: 'presa_in_carico', pagamento_posticipato: 'presa_in_carico', pagamento_allegato: 'presa_in_carico', pagamento_accettato: 'presa_in_carico', incasso_inviato: 'pagamento_accettato', emessa_da_pagare: 'incasso_inviato', emessa: 'incasso_inviato' };
              const prev = PREV[r.stato];
              return prev ? (
                <Button size="sm" variant="ghost" className="text-slate-400 hover:text-slate-600" onClick={() => handleStatoChange(r.id, prev)} data-testid="btn-retrocedi">
                  <ArrowLeft size={14} className="mr-1.5" /> Retrocedi a {STATI[prev]?.label}
                </Button>
              ) : null;
            })()}
            {isStaff && (
              <Button size="sm" variant="outline" onClick={handleOpenNotifica} className="border-slate-300" data-testid="btn-notifica-collab">
                <Mail size={14} className="mr-1.5" /> Notifica Collaboratore
              </Button>
            )}
            {/* Fabrick: Genera Link Pagamento */}
            {isStaff && r.premio && (
              <Button size="sm" variant="outline" onClick={handleOpenFabrick} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="btn-genera-link">
                <Link2 size={14} className="mr-1.5" /> Link Pagamento
              </Button>
            )}
            {isStaff && <Button size="sm" variant="ghost" className="text-red-400 ml-auto" onClick={() => handleDelete(r.id)} data-testid="btn-elimina"><Trash2 size={14} /></Button>}
          </div>

          {/* Payment Link Banner */}
          {r.payment_link && (
            <div className="px-3 sm:px-6 py-3 border-b border-slate-100 bg-blue-50">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <Link2 size={16} className="text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-blue-800 mb-0.5">Link di Pagamento Fabrick</p>
                  <a href={r.payment_link} target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-blue-600 hover:underline truncate block" data-testid="payment-link">{r.payment_link}</a>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300" onClick={() => { navigator.clipboard.writeText(r.payment_link); toast.success('Link copiato!'); }} data-testid="btn-copy-link">
                    Copia
                  </Button>
                  <a href={r.payment_link} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300" data-testid="btn-open-link">
                      <ExternalLink size={12} className="mr-1" /> Apri
                    </Button>
                  </a>
                  {isStaff && r.telefono_cliente && (
                    <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={handleSendLinkWA} data-testid="btn-send-link-wa">
                      <MessageCircle size={12} className="mr-1" /> Invia WA
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {r.note && (
            <div className="px-6 py-4 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Note</p>
              <p className="text-sm text-slate-700">{r.note}</p>
            </div>
          )}

          {/* Documents */}
          <div className="px-3 sm:px-6 py-4 border-b border-slate-100"
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('bg-blue-50'); }}
            onDragLeave={e => { e.preventDefault(); e.currentTarget.classList.remove('bg-blue-50'); }}
            onDrop={async e => {
              e.preventDefault();
              e.currentTarget.classList.remove('bg-blue-50');
              const files = Array.from(e.dataTransfer.files);
              if (files.length === 0) return;
              setUploadingType('drop');
              for (const file of files) {
                try {
                  const fd = new FormData(); fd.append('tipo', 'allegato'); fd.append('file', file);
                  const res = await api.post(`/richieste-emissione/${r.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  setSelected(res.data); toast.success(`${file.name} caricato!`);
                } catch { toast.error(`Errore upload ${file.name}`); }
              }
              setUploadingType(null); fetchAll();
            }}
            data-testid="documents-dropzone">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Documenti</p>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleUpload(r.id, 'allegato')} data-testid="btn-aggiungi-doc">
                <Upload size={12} className="mr-1" /> Aggiungi documento
              </Button>
            </div>
            {/* Drop zone hint */}
            <div className="mb-3 p-3 rounded-lg border-2 border-dashed border-slate-200 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-all cursor-pointer"
              onClick={() => handleUpload(r.id, 'allegato')} data-testid="drop-hint">
              <Upload size={20} className="mx-auto text-slate-300 mb-1" />
              <p className="text-xs text-slate-400">Trascina i file qui oppure clicca per selezionare</p>
            </div>
            {/* Quick upload buttons for document categories */}
            {isStaff && (
              <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-slate-100">
                {[
                  { tipo: 'certificato', label: 'Certificato', color: 'sky' },
                  { tipo: 'contratto', label: 'Contratto', color: 'emerald' },
                  { tipo: 'fattura', label: 'Fattura', color: 'violet' },
                  { tipo: 'assistenza_stradale', label: 'Ass. Stradale', color: 'amber' },
                  { tipo: 'infortunio', label: 'Infortunio', color: 'red' },
                  { tipo: 'polizza', label: 'Polizza', color: 'green' },
                ].map(cat => (
                  <Button key={cat.tipo} size="sm" variant="outline"
                    className={`h-6 text-[10px] px-2 border-${cat.color}-300 text-${cat.color}-700 hover:bg-${cat.color}-50`}
                    onClick={() => handleUpload(r.id, cat.tipo)} disabled={!!uploadingType}
                    data-testid={`btn-upload-${cat.tipo}`}>
                    <Upload size={10} className="mr-1" /> {uploadingType === cat.tipo ? '...' : cat.label}
                  </Button>
                ))}
              </div>
            )}
            {(r.documenti || []).length === 0 ? <p className="text-sm text-slate-400">Nessun documento</p> : (
              <div className="space-y-2">
                {r.documenti.filter(doc => {
                  // Hide ALL payment/financial docs from collaboratori
                  if (isCollaboratore && (doc.tipo === 'pagamento' || doc.tipo === 'pagamento_direzionale' || doc.tipo === 'pagamento_collaboratore')) return false;
                  return true;
                }).map(doc => {
                  const colors = { preventivo: '#3B82F6', pagamento: '#8B5CF6', pagamento_collaboratore: '#A855F7', pagamento_direzionale: '#1D4ED8', polizza: '#10B981', libretto_assicurare: '#F59E0B', documento_identita: '#6366F1', tessera_sanitaria: '#EC4899', patente: '#14B8A6', stato_famiglia: '#F97316', libretto_bersani: '#F97316', certificato: '#0EA5E9', contratto: '#059669', fattura: '#7C3AED', assistenza_stradale: '#D97706', infortunio: '#DC2626', allegato: '#64748B' };
                  const labels = { preventivo: 'Preventivo', pagamento: 'Pagamento', pagamento_collaboratore: 'Pag. Collaboratore', pagamento_direzionale: 'Pag. Direzionale', polizza: 'Polizza', libretto_assicurare: 'Libretto', documento_identita: 'Doc. Identità', tessera_sanitaria: 'Tessera Sanitaria', patente: 'Patente', stato_famiglia: 'Stato Famiglia', libretto_bersani: 'Libretto Bersani', certificato: 'Certificato', contratto: 'Contratto', fattura: 'Fattura', assistenza_stradale: 'Assistenza Stradale', infortunio: 'Infortunio', allegato: 'Allegato' };
                  return (
                    <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-slate-100" data-testid={`doc-${doc.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: (colors[doc.tipo] || '#64748B') + '15' }}>
                          <FileText size={16} style={{ color: colors[doc.tipo] || '#64748B' }} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700">{doc.filename}</p>
                          <p className="text-[11px] text-slate-400">{labels[doc.tipo] || doc.tipo} · {doc.uploaded_by} · {fmtDate(doc.uploaded_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={async () => {
                          try {
                            const res = await api.get(`/richieste-emissione/${r.id}/documento/${doc.id}`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(res.data);
                            const a = document.createElement('a'); a.href = url; a.download = doc.filename; a.click();
                            window.URL.revokeObjectURL(url);
                          } catch { toast.error('Errore download'); }
                        }} className="p-2 rounded-lg hover:bg-slate-100" data-testid={`download-doc-${doc.id}`}>
                          <Download size={16} className="text-slate-500" />
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm(`Eliminare "${doc.filename}"?`)) return;
                          try {
                            await api.delete(`/richieste-emissione/${r.id}/documento/${doc.id}`);
                            toast.success('Documento eliminato');
                            refreshSelected();
                          } catch { toast.error('Errore eliminazione'); }
                        }} className="p-2 rounded-lg hover:bg-red-50" data-testid={`delete-doc-${doc.id}`}>
                          <Trash2 size={14} className="text-slate-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Comunicazioni */}
          <div className="px-6 py-4 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Comunicazioni</p>
            {/* Messages list */}
            <div className="space-y-2 mb-3 max-h-[200px] overflow-y-auto">
              {(r.log || []).filter(l => l.tipo === 'comunicazione').map((entry, i) => (
                <div key={`${entry.data || ''}-${i}`} className="p-3 rounded-lg border border-slate-100" style={{ backgroundColor: entry.from_collab ? '#F0FDF4' : '#F8FAFC' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-semibold" style={{ color: entry.from_collab ? '#16A34A' : '#3B82F6' }}>{entry.utente}</span>
                    <span className="text-[10px] text-slate-400">{fmtDate(entry.data)}</span>
                    {entry.email_sent && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Email inviata</span>}
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{entry.messaggio || entry.azione}</p>
                </div>
              ))}
              {(r.log || []).filter(l => l.tipo === 'comunicazione').length === 0 && (
                <p className="text-xs text-slate-400 text-center py-2">Nessuna comunicazione</p>
              )}
            </div>
            {/* Send message */}
            <div className="flex gap-2">
              <Textarea value={comMsg} onChange={e => setComMsg(e.target.value)} placeholder="Scrivi un messaggio (es. Ci serve il codice fiscale...)" rows={2} className="text-sm flex-1" data-testid="com-msg-input"
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && comMsg.trim()) { e.preventDefault(); handleSendComunicazione(); } }} />
              <Button onClick={handleSendComunicazione} disabled={sendingCom || !comMsg.trim()} size="sm" className="self-end" data-testid="btn-send-com">
                {sendingCom ? '...' : <Send size={14} />}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Il messaggio viene salvato e inviato via email al collaboratore</p>
          </div>

          {/* Timeline */}
          <div className="px-6 py-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Cronologia</p>
            <div className="space-y-3">
              {(r.log || []).map((entry, i) => (
                <div key={`${entry.data || ''}-${i}`} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-slate-300 mt-1.5 flex-shrink-0" />
                  <div><p className="text-sm text-slate-700">{entry.azione}</p><p className="text-[11px] text-slate-400">{entry.utente} · {fmtDate(entry.data)}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Proposta Dialog */}
        <Dialog open={propostaOpen} onOpenChange={setPropostaOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Prendi in Carico</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Numero Proposta (opzionale)</Label>
                <Input value={numeroProposta} onChange={e => setNumeroProposta(e.target.value)} placeholder="Es. 12345678" className="mt-1" data-testid="input-num-proposta" />
              </div>
              <Button onClick={confirmPrendiInCarico} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-prendi-carico">
                <Shield size={14} className="mr-1.5" /> Conferma Presa in Carico
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Email Incasso Dialog */}
        <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail size={18} /> Invia Email Incasso</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Destinatario Email</Label>
                <Input value={emailForm.email_to} onChange={e => setEmailForm({ ...emailForm, email_to: e.target.value })} placeholder="email@compagnia.it" className="mt-1" data-testid="email-to" />
              </div>
              <div>
                <Label className="text-xs">Oggetto</Label>
                <Input value={emailForm.oggetto} onChange={e => setEmailForm({ ...emailForm, oggetto: e.target.value })} className="mt-1" data-testid="email-oggetto" />
              </div>
              <div>
                <Label className="text-xs">Corpo messaggio</Label>
                <Textarea value={emailForm.corpo} onChange={e => setEmailForm({ ...emailForm, corpo: e.target.value })} rows={6} className="mt-1 text-sm" data-testid="email-corpo" />
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 font-medium mb-1">Allegati automatici:</p>
                <div className="space-y-1">
                  {(selected?.documenti || []).map(d => (
                    <p key={d.id} className="text-xs text-slate-600 flex items-center gap-1.5"><Paperclip size={11} /> {d.filename} ({d.tipo})</p>
                  ))}
                  {(selected?.documenti || []).length === 0 && (
                    <p className="text-xs text-slate-400">Nessun documento da allegare</p>
                  )}
                </div>
              </div>
              <Button onClick={handleSendEmail} disabled={sendingEmail} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="btn-send-email">
                {sendingEmail ? 'Invio in corso...' : <><Send size={14} className="mr-1.5" /> Invia Email Incasso</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Notifica Collaboratore Dialog */}
        <Dialog open={notificaOpen} onOpenChange={setNotificaOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail size={18} /> Notifica Collaboratore</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Invia un'email di aggiornamento a <strong>{selected?.collaboratore_nome}</strong> sullo stato della richiesta.</p>
              <div>
                <Label className="text-xs">Messaggio</Label>
                <Textarea value={notificaMsg} onChange={e => setNotificaMsg(e.target.value)} rows={5} className="mt-1 text-sm" data-testid="notifica-msg" />
              </div>
              <Button onClick={handleSendNotifica} disabled={sendingNotifica} className="w-full" data-testid="btn-send-notifica">
                {sendingNotifica ? 'Invio...' : <><Send size={14} className="mr-1.5" /> Invia Notifica</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Fabrick Payment Link Dialog */}
        <Dialog open={fabrickOpen} onOpenChange={setFabrickOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 size={18} className="text-blue-600" /> Genera Link Pagamento</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Genera un link di pagamento Fabrick per <strong>{selected?.cliente_nome} {selected?.cliente_cognome}</strong>.</p>
              <div>
                <Label className="text-xs">Importo (EUR)</Label>
                <Input type="number" step="0.01" min="0.01" value={fabrickForm.importo} onChange={e => setFabrickForm({ ...fabrickForm, importo: e.target.value })} placeholder="450.00" className="mt-1" data-testid="fabrick-importo" />
              </div>
              <div>
                <Label className="text-xs">Email cliente (opzionale)</Label>
                <Input value={fabrickForm.email_cliente} onChange={e => setFabrickForm({ ...fabrickForm, email_cliente: e.target.value })} placeholder="cliente@email.it" className="mt-1" data-testid="fabrick-email" />
                <p className="text-[10px] text-slate-400 mt-1">Se inserita, Fabrick inviera' il link anche via email</p>
              </div>
              <Button onClick={handleGeneraLinkPagamento} disabled={generatingLink} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="btn-genera-fabrick">
                {generatingLink ? 'Generazione...' : <><Link2 size={14} className="mr-1.5" /> Genera Link Pagamento</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ═══ LIST VIEW ═══
  return (
    <div className="p-3 sm:p-6 md:p-8" data-testid="richieste-page">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Richieste Emissione</h1>
          <p className="text-sm text-slate-500 mt-0.5">{filtered.length} richieste</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800" size="sm" data-testid="btn-nuova-richiesta">
          <Plus size={16} className="mr-1.5" /> <span className="hidden sm:inline">Nuova </span>Richiesta
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0.5 sm:gap-1 mb-4 sm:mb-6 border-b border-slate-200 overflow-x-auto no-scrollbar">
        <button onClick={() => setActiveTab('richieste')}
          className={`px-2.5 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${activeTab === 'richieste' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          data-testid="tab-richieste">Richieste</button>
        <button onClick={() => setActiveTab('pagamenti-posticipati')}
          className={`px-2.5 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-all flex items-center gap-1 sm:gap-1.5 whitespace-nowrap ${activeTab === 'pagamenti-posticipati' ? 'border-amber-600 text-amber-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          data-testid="tab-pagamenti-posticipati">
          <Hourglass size={14} /> <span className="hidden sm:inline">Pagamenti </span>Posticipati
          {richieste.filter(r => r.stato === 'pagamento_posticipato').length > 0 && (
            <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1">
              {richieste.filter(r => r.stato === 'pagamento_posticipato').length}
            </span>
          )}
        </button>
        <button onClick={() => setActiveTab('ricerca-veicoli')}
          className={`px-2.5 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-all flex items-center gap-1 sm:gap-1.5 whitespace-nowrap ${activeTab === 'ricerca-veicoli' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          data-testid="tab-ricerca-veicoli"><Search size={14} /> <span className="hidden sm:inline">Ricerca </span>Veicoli</button>
      </div>

      {activeTab === 'ricerca-veicoli' ? (
        <RicercaVeicoli />
      ) : activeTab === 'pagamenti-posticipati' ? (
        <div data-testid="pagamenti-posticipati-section">
          <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={16} className="text-amber-600" />
              <p className="text-sm font-semibold text-amber-800">Pagamenti in Attesa</p>
            </div>
            <p className="text-xs text-amber-600">Richieste con pagamento posticipato. {isCollaboratore ? 'Allega il pagamento per completare.' : 'In attesa che il collaboratore alleghi il pagamento.'}</p>
          </div>
          <div className="space-y-2">
            {richieste.filter(r => r.stato === 'pagamento_posticipato').map(r => (
              <button key={r.id} onClick={() => setSelected(r)}
                className="w-full text-left bg-white rounded-xl border border-amber-200 p-4 hover:shadow-sm hover:border-amber-300 transition-all flex items-center gap-4"
                data-testid={`posticipato-row-${r.id}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-100">
                  <Hourglass size={18} className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-slate-900">{r.cliente_nome} {r.cliente_cognome}</span>
                    <StatoBadge stato={r.stato} />
                    {r.numero_proposta && <span className="text-[11px] font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">#{r.numero_proposta}</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    {r.compagnia} · {r.tipo_polizza}{r.targa ? ` · ${r.targa}` : ''}{r.premio ? ` · ${r.premio}/${r.tipo_premio || 'ann.'}` : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0 hidden sm:block">
                  <p className="text-[11px] text-slate-400">{fmtDate(r.updated_at || r.created_at)}</p>
                  <p className="text-[11px] text-amber-600 font-medium">In attesa pagamento</p>
                </div>
                <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
              </button>
            ))}
            {richieste.filter(r => r.stato === 'pagamento_posticipato').length === 0 && (
              <div className="text-center py-16">
                <div className="inline-block p-6 rounded-full bg-green-50 mb-4"><CheckCircle size={40} className="text-green-400" /></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Nessun pagamento posticipato</h3>
                <p className="text-sm text-slate-500">Tutti i pagamenti sono stati allegati</p>
              </div>
            )}
          </div>
        </div>
      ) : (
      <>
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 mb-4 sm:mb-6">
        {Object.entries(STATI).filter(([k]) => k !== 'rifiutata').map(([key, val]) => {
          const Icon = val.icon; const cnt = stats[key]?.count || 0;
          return (
            <button key={key} onClick={() => setFilterStato(filterStato === key ? '' : key)}
              className={`p-3 rounded-xl border transition-all text-left ${filterStato === key ? 'ring-2 ring-offset-1' : 'hover:shadow-sm'}`}
              style={{ borderColor: filterStato === key ? val.color : '#E2E8F0', ringColor: val.color }} data-testid={`stat-${key}`}>
              <div className="flex items-center gap-1.5 mb-1"><Icon size={13} style={{ color: val.color }} /><span className="text-[10px] font-medium text-slate-500">{val.label}</span></div>
              <span className="text-xl font-bold text-slate-900">{cnt}</span>
            </button>
          );
        })}
      </div>

      {/* Search + Date Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca cliente, compagnia, targa, proposta..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-input" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-slate-500">Dal</span>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-xs w-[130px]" data-testid="filter-date-from" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-slate-500">Al</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-xs w-[130px]" data-testid="filter-date-to" />
          </div>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-[11px] text-red-500 hover:text-red-700 px-1" data-testid="clear-dates">Pulisci</button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.map(r => (
          <button key={r.id} onClick={() => setSelected(r)}
            className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm hover:border-slate-300 transition-all flex items-center gap-4"
            data-testid={`richiesta-row-${r.id}`}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (STATI[r.stato]?.bg || '#F1F5F9') }}>
              <User size={18} style={{ color: STATI[r.stato]?.color || '#64748B' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-slate-900">{r.cliente_nome} {r.cliente_cognome}</span>
                <StatoBadge stato={r.stato} />
                {r.numero_proposta && <span className="text-[11px] font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">#{r.numero_proposta}</span>}
                {r.numero_preventivo && <span className="text-[11px] font-mono bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">Prev. {r.numero_preventivo}</span>}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 truncate">
                {r.compagnia} · {r.tipo_polizza}{r.targa ? ` · ${r.targa}` : ''}{r.premio ? ` · €${r.premio}/${r.tipo_premio || 'ann.'}` : ''}
              </p>
            </div>
            <div className="text-right flex-shrink-0 hidden sm:block">
              <p className="text-[11px] text-slate-400">{fmtDate(r.created_at)}</p>
              <p className="text-[11px] text-slate-400">{isStaff ? `da ${r.collaboratore_nome}` : ''}</p>
            </div>
            <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <div className="inline-block p-6 rounded-full bg-slate-100 mb-4"><FileText size={40} className="text-slate-400" /></div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">{richieste.length ? 'Nessun risultato' : 'Nessuna richiesta'}</h3>
          <p className="text-sm text-slate-500">{richieste.length ? 'Prova a cambiare i filtri' : 'Crea la tua prima richiesta di emissione'}</p>
        </div>
      )}
      </>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuova Richiesta Emissione</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Nome cliente</Label><Input value={form.cliente_nome} onChange={e => setForm({ ...form, cliente_nome: e.target.value })} required className="h-9 text-sm mt-1" data-testid="form-nome" /></div>
              <div><Label className="text-xs">Cognome cliente</Label><Input value={form.cliente_cognome} onChange={e => setForm({ ...form, cliente_cognome: e.target.value })} required className="h-9 text-sm mt-1" data-testid="form-cognome" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Compagnia</Label><Input value={form.compagnia} onChange={e => setForm({ ...form, compagnia: e.target.value })} required placeholder="Es. Allianz, UnipolSai..." className="h-9 text-sm mt-1" data-testid="form-compagnia" /></div>
              <div>
                <Label className="text-xs">Tipo Polizza</Label>
                <Select value={form.tipo_polizza} onValueChange={v => setForm({ ...form, tipo_polizza: v })}>
                  <SelectTrigger className="h-9 text-sm mt-1" data-testid="form-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPI_POLIZZA.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Premio (€)</Label>
                <div className="flex gap-1.5 mt-1">
                  <Input value={form.premio} onChange={e => setForm({ ...form, premio: e.target.value })} placeholder="450.00" className="h-9 text-sm flex-1" data-testid="form-premio" />
                  <select value={form.tipo_premio} onChange={e => setForm({ ...form, tipo_premio: e.target.value })} className="h-9 px-2 text-xs rounded-md border border-slate-200 bg-white" data-testid="form-tipo-premio">
                    <option value="annuale">Annuale</option>
                    <option value="semestrale">Semestrale</option>
                  </select>
                </div>
              </div>
              <div><Label className="text-xs">Targa</Label><Input value={form.targa} onChange={e => setForm({ ...form, targa: e.target.value.toUpperCase() })} placeholder="AB123CD" className="h-9 text-sm mt-1" data-testid="form-targa" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Telefono cliente</Label><Input value={form.telefono_cliente} onChange={e => setForm({ ...form, telefono_cliente: e.target.value })} placeholder="+39 333 1234567" className="h-9 text-sm mt-1" data-testid="form-telefono" /></div>
              {isStaff && (
                <div><Label className="text-xs text-red-600">Premio Riservato (€)</Label><Input value={form.premio_riservato} onChange={e => setForm({ ...form, premio_riservato: e.target.value })} placeholder="380.00" className="h-9 text-sm mt-1 border-red-200" data-testid="form-premio-riservato" /></div>
              )}
            </div>
            <div><Label className="text-xs">Note</Label><Textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} className="text-sm mt-1" data-testid="form-note" /></div>

            {/* Assegna a collaboratore (solo staff) */}
            {isStaff && collaboratori.length > 0 && (
              <div>
                <Label className="text-xs">Assegna a collaboratore (opzionale)</Label>
                <select value={form.assegna_a} onChange={e => setForm({ ...form, assegna_a: e.target.value })} className="w-full h-9 px-3 text-sm rounded-md border border-slate-200 bg-white mt-1" data-testid="form-assegna-a">
                  <option value="">-- Nessuno (mia richiesta) --</option>
                  {collaboratori.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
                </select>
              </div>
            )}

            {/* Documenti strutturati */}
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Documenti</p>
              <DocUploadField label="Libretto da assicurare" fileKey="libretto" required />
              <DocUploadField label="Documento d'identità" fileKey="documento_identita" />
              <DocUploadField label="Tessera sanitaria" fileKey="tessera_sanitaria" />
              <DocUploadField label="Patente" fileKey="patente" />
              <div className="pt-2 border-t border-slate-100 space-y-2">
                <p className="text-xs font-medium text-slate-600">Bersani</p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="radio" name="bersani" value="no" checked={form.bersani === 'no'} onChange={() => setForm({ ...form, bersani: 'no' })} className="accent-slate-600" /> No</label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="radio" name="bersani" value="stesso" checked={form.bersani === 'stesso'} onChange={() => setForm({ ...form, bersani: 'stesso' })} className="accent-slate-600" /> Bersani stesso</label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="radio" name="bersani" value="diverso" checked={form.bersani === 'diverso'} onChange={() => setForm({ ...form, bersani: 'diverso' })} className="accent-slate-600" /> Bersani diverso</label>
                </div>
                {form.bersani !== 'no' && (
                  <div className="space-y-2 pl-2 border-l-2 border-orange-200 ml-1">
                    {form.bersani === 'diverso' && <DocUploadField label="Stato di famiglia" fileKey="stato_famiglia" />}
                    <DocUploadField label="Libretto targa Bersani" fileKey="libretto_bersani" />
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs">Allega Preventivo o Preventivass, o inserisci numero di preventivo</Label>
              <Input value={form.numero_preventivo || ''} onChange={e => setForm({ ...form, numero_preventivo: e.target.value })} placeholder="N. preventivo (opzionale)" className="h-9 text-sm mt-1 mb-2" data-testid="form-num-preventivo" />
              <div className="border border-dashed border-slate-300 rounded-lg p-3 text-center cursor-pointer hover:border-slate-400" onClick={() => fileRef.current?.click()}>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={e => setFormFile(e.target.files[0])} />
                {formFile ? <div className="flex items-center justify-center gap-2 text-sm text-slate-700"><Paperclip size={14} /> {formFile.name} <button type="button" onClick={e => { e.stopPropagation(); setFormFile(null); }} className="text-red-400"><XCircle size={14} /></button></div>
                  : <p className="text-xs text-slate-400"><Upload size={16} className="inline mr-1" /> PDF o immagine del preventivo</p>}
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="btn-submit-richiesta">{submitting ? 'Invio...' : 'Invia Richiesta'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RichiesteEmissione;
